import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Wand2, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAIGenerationEnabled } from '../hooks/useAIGenerationEnabled';
import { supabase } from '../lib/supabase';
import { logGenerationError } from '../lib/errorLogger';
import { WIZARD_PATHS, wizardUrl } from '../lib/wizardRoutes';
import { DashboardHeader } from '../components/DashboardHeader';

const TEXT_PROCESSING_TIMEOUT = 90000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

type ProcessingMode = 'grammar' | 'rewrite';

interface FetchWithTimeoutOptions {
  timeout: number;
  signal?: AbortSignal;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit & FetchWithTimeoutOptions
): Promise<Response> {
  const { timeout, signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Timeout: Het duurt te lang, probeer opnieuw');
    }
    throw error;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function TextImport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEnabled: aiGenerationEnabled, loading: aiCheckLoading } = useAIGenerationEnabled();
  const [text, setText] = useState('');
  const [processingMode, setProcessingMode] = useState<ProcessingMode>('grammar');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    if (!aiCheckLoading && !aiGenerationEnabled) {
      alert('AI generatie is tijdelijk uitgeschakeld');
      navigate('/dashboard');
    }
  }, [aiGenerationEnabled, aiCheckLoading, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!text.trim()) {
      setError('Voer eerst je tekst in');
      return;
    }

    if (text.trim().length < 100) {
      setError('Je tekst moet minimaal 100 karakters bevatten');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Altijd een nieuwe sessie aanmaken: hergebruik van een idea_selection-sessie
      // mengde gegenereerde ideeën met text-import en zorgde voor verwarrende UX.
      console.log('[TextImport] Creating new session for user:', user.id);
      const { data: createdSession, error: sessionError } = await supabase
        .from('sessions')
        .insert({
          user_id: user.id,
          state: 'idea_approved',
        })
        .select()
        .single();

      if (sessionError || !createdSession) {
        console.error('[TextImport] Session creation error:', sessionError);
        throw new Error(sessionError?.message || 'Kon geen sessie aanmaken');
      }

      const newSession = createdSession;
      console.log('[TextImport] Session created:', newSession.id);

      const { data: newIdea, error: ideaError } = await supabase
        .from('blog_ideas')
        .insert({
          session_id: newSession.id,
          user_id: user.id,
          source: 'text_import',
          idea_title: 'Geimporteerde tekst',
          idea_content: text.substring(0, 500),
          idea_reason: `Gebruiker heeft eigen tekst geimporteerd (${processingMode === 'grammar' ? 'grammatica verbeteren' : 'volledig herschrijven'})`,
          status: 'approved',
          decided_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (ideaError || !newIdea) {
        console.error('Idea creation error:', ideaError);
        console.error('Idea creation error details:', {
          message: ideaError?.message,
          code: ideaError?.code,
          details: ideaError?.details,
          hint: ideaError?.hint,
        });
        throw new Error(ideaError?.message || 'Kon idee niet opslaan');
      }

      await supabase
        .from('sessions')
        .update({ approved_idea_id: newIdea.id })
        .eq('id', newSession.id);

      const generateType = processingMode === 'grammar' ? 'text_grammar_fix' : 'text_full_rewrite';

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      let lastError: Error | null = null;
      let result: any = null;

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          setRetryAttempt(attempt);
          console.log(`[TextImport] Attempt ${attempt}/${MAX_RETRY_ATTEMPTS}`);

          // Check if article_content was already generated (e.g., after browser timeout)
          const { data: existingSession } = await supabase
            .from('sessions')
            .select('article_content')
            .eq('id', newSession.id)
            .maybeSingle();

          if (existingSession?.article_content) {
            console.log(`[TextImport] Article content already exists, skipping OpenAI call`);
            result = { success: true, message: 'Article already generated' };
            lastError = null;
            break;
          }

          const response = await fetchWithTimeout(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                type: generateType,
                session_id: newSession.id,
                user_id: user.id,
                data: {
                  original_text: text,
                  company_name: user.company_name,
                  website_url: user.website_url,
                },
              }),
              timeout: TEXT_PROCESSING_TIMEOUT,
            }
          );

          const responseText = await response.text();

          if (!responseText || responseText.trim() === '') {
            throw new Error(`API gaf een lege response (status ${response.status})`);
          }

          try {
            result = JSON.parse(responseText);
          } catch (parseError) {
            console.error('[TextImport] JSON parse failed. Response text:', responseText);
            const parseMsg = parseError instanceof Error ? parseError.message : String(parseError);
            throw new Error(`Ongeldige response van API: ${parseMsg}`);
          }

          if (!response.ok) {
            console.error(`[TextImport] API error on attempt ${attempt}:`, result);
            const serverError = new Error(result.message || result.error || 'Fout bij het verwerken van de tekst') as Error & {
              serverLogged?: boolean;
              category?: string;
            };
            serverError.serverLogged = result.logged === true;
            serverError.category = result.category;
            throw serverError;
          }

          console.log(`[TextImport] Success on attempt ${attempt}`);
          lastError = null;
          break;
        } catch (error) {
          lastError = error as Error;
          console.error(`[TextImport] Error on attempt ${attempt}:`, error);

          const errorMessage = error instanceof Error ? error.message : String(error);
          const errAny = error as Error & { serverLogged?: boolean; category?: string };
          const isAbort = error instanceof Error && error.name === 'AbortError';
          const isTimeout = isAbort || /timeout|timed out/i.test(errorMessage) || errAny.category === 'TIMEOUT';
          const isNetwork = !errAny.serverLogged && (errorMessage.includes('fetch') || errorMessage.includes('network'));

          // Skip client-side logging als server al een user_errors-row schreef.
          if (!errAny.serverLogged) {
            await logGenerationError({
              generationType: 'blog_content',
              errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'API_ERROR',
              errorMessage: errorMessage,
              technicalError: error instanceof Error ? error.stack : undefined,
              retryAttempt: attempt - 1,
              sessionId: newSession.id,
              additionalContext: {
                service: 'openai',
                apiType: 'text_import',
                processingMode,
                timeout: TEXT_PROCESSING_TIMEOUT,
                textLength: text.length,
                maxRetries: MAX_RETRY_ATTEMPTS,
                attemptNumber: attempt,
              },
            });
          }

          // Geen retry bij timeouts of server-side gelogde fouten (primary+fallback faalden al).
          if (isTimeout || errAny.serverLogged) {
            console.log('[TextImport] Niet retryen: timeout of server-side gefaald.');
            break;
          }

          if (attempt < MAX_RETRY_ATTEMPTS) {
            console.log(`[TextImport] Waiting ${RETRY_DELAY}ms before retry...`);
            await sleep(RETRY_DELAY);
          }
        }
      }

      if (lastError) {
        throw new Error(
          `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt. Probeer het later opnieuw of neem contact op met support.`
        );
      }

      if (!result) {
        throw new Error('Geen resultaat ontvangen van AI');
      }

      setRetryAttempt(0);

      // Edge function already updated the session with article_content
      // No need to update again here

      await supabase
        .from('sessions')
        .update({ state: 'article_generated' })
        .eq('id', newSession.id)
        .eq('state', 'idea_approved');

      navigate(wizardUrl(WIZARD_PATHS.articleEdit, newSession.id));
    } catch (err: any) {
      console.error('Error processing text:', err);
      setError(err.message || 'Er ging iets mis bij het verwerken van je tekst');
      setRetryAttempt(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <DashboardHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-[#231F20] mb-4">
            Plak je tekst
          </h2>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Plak hier je bestaande blog tekst of artikel..."
            className="w-full min-h-[300px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent outline-none resize-y"
            disabled={loading}
          />
          <p className="text-sm text-gray-500 mt-2">
            Minimaal 100 karakters • {text.length} karakters
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-[#231F20] mb-4">
            Hoe wil je de tekst verwerken?
          </h2>

          <div className="space-y-3">
            <label
              className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                processingMode === 'grammar'
                  ? 'border-[#FF6B35] bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="processingMode"
                value="grammar"
                checked={processingMode === 'grammar'}
                onChange={(e) => setProcessingMode(e.target.value as ProcessingMode)}
                className="mt-1 w-5 h-5 text-[#FF6B35] border-gray-300 focus:ring-[#FF6B35]"
                disabled={loading}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Wand2 className="w-5 h-5 text-[#FF6B35]" />
                  <span className="font-semibold text-[#231F20]">
                    Grammatica & Stijl Verbeteren
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  AI verbetert grammatica, spelling, structuur en opmaak, maar behoudt de essentie van je tekst
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                processingMode === 'rewrite'
                  ? 'border-[#FF6B35] bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="processingMode"
                value="rewrite"
                checked={processingMode === 'rewrite'}
                onChange={(e) => setProcessingMode(e.target.value as ProcessingMode)}
                className="mt-1 w-5 h-5 text-[#FF6B35] border-gray-300 focus:ring-[#FF6B35]"
                disabled={loading}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="w-5 h-5 text-[#FF6B35]" />
                  <span className="font-semibold text-[#231F20]">
                    Volledig Herschrijven
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  AI gebruikt je tekst als outline en schrijft een compleet nieuw artikel met betere structuur en flow
                </p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4">
          <Link
            to="/dashboard/new-blog"
            className="btn-secondary"
          >
            Annuleren
          </Link>
          <button
            onClick={handleSubmit}
            disabled={loading || !text.trim() || text.trim().length < 100}
            className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Verwerken...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5" />
                Verwerk tekst
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}
